import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './Transfers.css';
import TransactionTypeModal from './TransactionTypeModal';

function Transfers() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [transfers, setTransfers] = useState([]);
  const [pairedTransfers, setPairedTransfers] = useState([]);
  const [filteredTransfers, setFilteredTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Filters & Grouping
  const [filterPaid, setFilterPaid] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [groupBy, setGroupBy] = useState('none');
  const [categories, setCategories] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const user = auth.currentUser;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Load transfers for selected month
  useEffect(() => {
    if (!user) return;
    
    setLoading(true);
    const monthStr = String(selectedMonth + 1).padStart(2, '0');
    const monthKey = `${selectedYear}-${monthStr}`;
    
    const loadTransfers = async () => {
      try {
        const transactionsRef = collection(db, 'users', user.uid, 'transactions', monthKey, 'items');
        const snapshot = await getDocs(transactionsRef);
        
        // Filter only Transfer type transactions
        const transfersData = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(t => t.type === 'Transfer');
        
        setTransfers(transfersData);
        
        // Pair transfers by their 'transactionId' field
        const paired = pairTransfersByTransactionId(transfersData);
        setPairedTransfers(paired);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(paired.map(t => t.category).filter(c => c))].sort();
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading transfers:', error);
        setLoading(false);
      }
    };
    
    loadTransfers();
  }, [user, selectedYear, selectedMonth]);

  // Pair transfers by transaction ID
  const pairTransfersByTransactionId = (transfersData) => {
    const grouped = {};
    
    // Group by transactionId field
    transfersData.forEach(transfer => {
      const transactionId = transfer.transactionId || transfer.id;
      if (!grouped[transactionId]) {
        grouped[transactionId] = [];
      }
      grouped[transactionId].push(transfer);
    });
    
    // Create paired transfer objects
    const paired = Object.keys(grouped).map(transactionId => {
      const pair = grouped[transactionId];
      
      if (pair.length === 2) {
        // Determine debit and credit based on amount sign
        const debit = pair.find(t => parseFloat(t.amount) < 0) || pair[0];
        const credit = pair.find(t => parseFloat(t.amount) > 0) || pair[1];
        
        return {
          transactionId,
          date: debit.date || credit.date,
          name: debit.name || credit.name,
          category: debit.category || credit.category,
          fromAccount: debit.account,
          toAccount: credit.account,
          amount: Math.abs(parseFloat(debit.amount) || parseFloat(credit.amount)),
          period: debit.period || credit.period,
          paid: debit.paid && credit.paid,
          debitTransaction: debit,
          creditTransaction: credit
        };
      } else {
        // Unpaired transfer
        const single = pair[0];
        return {
          transactionId,
          date: single.date,
          name: single.name,
          category: single.category,
          fromAccount: single.account,
          toAccount: '-',
          amount: Math.abs(parseFloat(single.amount)),
          period: single.period,
          paid: single.paid,
          debitTransaction: single,
          creditTransaction: null
        };
      }
    });
    
    // Sort by date descending
    paired.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date.localeCompare(a.date);
    });
    
    return paired;
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...pairedTransfers];
    
    // Paid filter
    if (filterPaid !== 'all') {
      const isPaid = filterPaid === 'paid';
      filtered = filtered.filter(t => t.paid === isPaid);
    }
    
    // Category filter (multi-select)
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(t => selectedCategories.includes(t.category));
    }
    
    setFilteredTransfers(filtered);
  }, [pairedTransfers, filterPaid, selectedCategories]);

  // Auto-expand all groups when groupBy changes
  useEffect(() => {
    if (groupBy !== 'none') {
      const groups = getGroupedTransfers();
      const allExpanded = {};
      Object.keys(groups).forEach(key => {
        allExpanded[key] = true;
      });
      setExpandedGroups(allExpanded);
    }
  }, [groupBy, filteredTransfers]);

  const resetFilters = () => {
    setFilterPaid('all');
    setSelectedCategories([]);
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const selectAllCategories = () => {
    setSelectedCategories([...categories]);
  };

  const clearAllCategories = () => {
    setSelectedCategories([]);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const toggleRowExpansion = (transactionId) => {
    setExpandedRows(prev => ({
      ...prev,
      [transactionId]: !prev[transactionId]
    }));
  };

  const formatAmount = (amount) => {
  const num = parseFloat(amount) || 0;
  const absNum = Math.abs(num);
  
  const formatted = absNum.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  if (num < 0) {
    return `(£${formatted})`;
  }
  return `£${formatted}`;
};

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Input format: DD-MM-YYYY
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month) - 1;
    
    return `${day} ${monthNames[monthIndex]} ${year}`;
  };

  // Group transfers
  const getGroupedTransfers = () => {
    if (groupBy === 'none') {
      return { 'All Transfers': filteredTransfers };
    }

    const grouped = {};
    filteredTransfers.forEach(transfer => {
      let key = '';
      
      if (groupBy === 'category') {
        key = transfer.category || 'Uncategorized';
      } else if (groupBy === 'account') {
        key = transfer.fromAccount || 'Unknown Account';
      }
      
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(transfer);
    });

    return grouped;
  };

  const calculateGroupTotal = (groupTransfers) => {
    return groupTransfers.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
  };

  const formatTotal = (total) => {
  const num = parseFloat(total) || 0;
  const absNum = Math.abs(num);
  
  const formatted = absNum.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  if (num < 0) {
    return `(£${formatted})`;
  }
  return `£${formatted}`;
};

  const groupedData = getGroupedTransfers();

  return (
    <div className="transfers-container">
      {/* Header */}
      <div className="transfers-header">
        <h2>Transfers</h2>
        <div className="header-right">
          <div className="year-selector">
            <button className="year-btn" onClick={() => setSelectedYear(selectedYear - 1)}>◀</button>
            <span className="year-display">{selectedYear}</span>
            <button className="year-btn" onClick={() => setSelectedYear(selectedYear + 1)}>▶</button>
          </div>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>+ Add Transfer</button>
        </div>
      </div>
      
      {/* Month Tabs */}
      <div className="month-tabs">
        {months.map((month, index) => (
          <button
            key={month}
            className={`month-tab ${selectedMonth === index ? 'active' : ''}`}
            onClick={() => setSelectedMonth(index)}
          >
            {month}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterPaid} onChange={(e) => setFilterPaid(e.target.value)}>
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>
        
        <div className="filter-group category-filter">
          <label>Categories:</label>
          <div className="category-dropdown">
            <button className="category-dropdown-btn">
              {selectedCategories.length === 0 ? 'All Categories' : `${selectedCategories.length} selected`}
            </button>
            <div className="category-dropdown-content">
              <div className="category-actions">
                <button onClick={selectAllCategories}>Select All</button>
                <button onClick={clearAllCategories}>Clear All</button>
              </div>
              {categories.map(cat => (
                <label key={cat} className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat)}
                    onChange={() => toggleCategory(cat)}
                  />
                  <span>{cat}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="filter-group">
          <label>Group By:</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="none">No Grouping</option>
            <option value="category">Category</option>
            <option value="account">Account</option>
          </select>
        </div>
        
        <button className="btn-reset" onClick={resetFilters}>Reset Filters</button>
        
        <div className="transfer-count">
          Showing {filteredTransfers.length} of {pairedTransfers.length} transfers
        </div>
      </div>

      {/* Transfers List */}
      {loading ? (
        <div className="loading-state">Loading transfers...</div>
      ) : filteredTransfers.length === 0 ? (
        <div className="empty-state">
          {pairedTransfers.length === 0 ? 'No transfers for this month.' : 'No transfers match your filters.'}
        </div>
      ) : (
        <div className="grouped-tables">
          {Object.keys(groupedData).map((groupName) => {
            const groupTransfers = groupedData[groupName];
            const groupTotal = calculateGroupTotal(groupTransfers);
            const isGroupExpanded = expandedGroups[groupName] !== false;
            
            return (
              <div key={groupName} className="group-section">
                {groupBy !== 'none' && (
                  <div className="group-header" onClick={() => toggleGroup(groupName)} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{isGroupExpanded ? '▼' : '▶'}</span>
                      <h3>{groupName}</h3>
                    </div>
                    <span className="group-total">Total: {formatTotal(groupTotal)}</span>
                  </div>
                )}
                
                {isGroupExpanded && (
                  <table className="transfers-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>DATE</th>
                        <th>NAME</th>
                        <th>CATEGORY</th>
                        <th>ACCOUNTS</th>
                        <th>PERIOD</th>
                        <th className="right">AMOUNT</th>
                        <th className="center">PAID</th>
                        <th className="center">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupTransfers.map((transfer) => {
                        const isExpanded = expandedRows[transfer.transactionId];
                        
                        return (
                          <React.Fragment key={transfer.transactionId}>
                            {/* Main Row */}
                            <tr 
                              className={`transfer-row ${transfer.paid ? '' : 'unpaid'} ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => toggleRowExpansion(transfer.transactionId)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td className="expand-cell">
                                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                              </td>
                              <td>{formatDate(transfer.date)}</td>
                              <td><strong>{transfer.name || '-'}</strong></td>
                              <td>{transfer.category || '-'}</td>
                              <td className="accounts-cell">
                                <span className="account-flow">
                                  {transfer.fromAccount} ⟷ {transfer.toAccount}
                                </span>
                              </td>
                              <td>{transfer.period || '-'}</td>
                              <td className="right amount transfer">
                                {formatAmount(transfer.amount)}
                              </td>
                              <td className="center">
                                {transfer.paid ? '✅' : '❌'}
                              </td>
                              <td className="center actions">
                                <button className="btn-delete" onClick={(e) => e.stopPropagation()}>Delete</button>
                              </td>
                            </tr>
                            
                           {/* Expanded Details Row */}
                          {isExpanded && (
                            <>
                              <tr className="transfer-details-row debit-row">
                                <td></td>
                                <td>{formatDate(transfer.debitTransaction?.date || transfer.date)}</td>
                                <td><strong>{transfer.debitTransaction?.name || transfer.name || '-'}</strong></td>
                                <td>{transfer.debitTransaction?.category || transfer.category || '-'}</td>
                                <td className="detail-label">├─ Debit: {transfer.fromAccount}</td>
                                <td>{transfer.debitTransaction?.period || transfer.period || '-'}</td>
                                <td className="right amount debit-amount">-{formatAmount(transfer.amount)}</td>
                                <td className="center">{transfer.debitTransaction?.paid ? '✅' : '❌'}</td>
                                <td className="center"></td>
                              </tr>
                              <tr className="transfer-details-row credit-row">
                                <td></td>
                                <td>{formatDate(transfer.creditTransaction?.date || transfer.date)}</td>
                                <td><strong>{transfer.creditTransaction?.name || transfer.name || '-'}</strong></td>
                                <td>{transfer.creditTransaction?.category || transfer.category || '-'}</td>
                                <td className="detail-label">└─ Credit: {transfer.toAccount}</td>
                                <td>{transfer.creditTransaction?.period || transfer.period || '-'}</td>
                                <td className="right amount credit-amount">+{formatAmount(transfer.amount)}</td>
                                <td className="center">{transfer.creditTransaction?.paid ? '✅' : '❌'}</td>
                                <td className="center"></td>
                              </tr>
                            </>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    {groupBy !== 'none' && (
                      <tfoot>
                        <tr className="group-totals-row">
                          <td colSpan="6"><strong>{groupName} TOTAL</strong></td>
                          <td className="right">
                            <strong>{formatTotal(groupTotal)}</strong>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}
              </div>
            );
          })}
          
          {/* Grand Total */}
          {groupBy !== 'none' && (
            <div className="grand-total">
              <strong>GRAND TOTAL: {formatTotal(filteredTransfers.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0))}</strong>
            </div>
          )}
          
          {/* No Grouping Total */}
          {groupBy === 'none' && (
            <div className="grand-total">
              <strong>TOTAL: {formatTotal(filteredTransfers.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0))}</strong>
            </div>
          )}
        </div>
      )}

      {/* Add Transfer Modal */}
      {showAddModal && (
        <TransactionTypeModal onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}

export default Transfers;